import { ReactNode, useCallback, useState } from "react";
import ImageApp from "../ui/image-app";
import { IMAGES } from "@/assets/images";
import { Button, ActionIcon } from "rizzui";
import { MdPushPin } from "react-icons/md";
import { ImCreditCard } from "react-icons/im";
import { cn } from "@/lib/utils";
import { EXeroPaymentMethod } from "@/utilities/enums/Payment";
import { IoMdClose } from "react-icons/io";

interface IPaymentMethodProps {
  initMethod?: EXeroPaymentMethod;
  onPayNowClick: () => void;
  openModalCreditCard: () => void;
  onClose: () => void;
}

type ContentProps = {
  title: string;
  description: string;
};

const PreferenceNotice = ({ title, description }: ContentProps) => (
  <div className="mt-6 p-4 rounded-md bg-pink-100">
    <div className="flex flex-row justify-between items-center">
      <div className="w-full">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm mt-1 text-gray-700">{description}</p>
      </div>
      <ImageApp
        inApp
        src={IMAGES.PAY_PREFERRED}
        alt="pay-preferred"
        className="max-h-12 object-contain"
      />
    </div>
  </div>
);

const PaymentInstructions = ({ title, description }: ContentProps) => (
  <div className="mt-6 p-4 rounded-md bg-gray-100">
    <h3 className="text-lg font-semibold">{title}</h3>
    <p className="text-sm mt-1 text-gray-700">{description}</p>
  </div>
);

const getContent = (method: EXeroPaymentMethod): ReactNode => {
  switch (method) {
    case EXeroPaymentMethod.CREDIT_CARD:
      return (
        <>
          <PreferenceNotice
            title="We prefer PayNow!"
            description="When you pay using PayNow, ProjectSG doesn't incur any fees, which means that we can keep our products cheap for you."
          />
          <PaymentInstructions
            title="Credit Card Payment Instructions"
            description="After submitting the order below, you will be prompted to make payment via Credit Card through our payment processing company."
          />
        </>
      );

    case EXeroPaymentMethod.PAY_NOW:
      return (
        <>
          <PreferenceNotice
            title="Save with PayNow!"
            description="Avoid extra charges by paying with PayNow. An admin fee is payable for credit card payments."
          />
          <PaymentInstructions
            title="PayNow Payment Instructions"
            description="After submitting the order below, you will be asked to scan the PayNow QR code to make the payment."
          />
        </>
      );

    default:
      return null;
  }
};

const SelectPaymentMethod = (props: IPaymentMethodProps) => {
  const {
    initMethod,
    onPayNowClick,
    openModalCreditCard,
    onClose,
  } = props;

  const [selectedMethod, setSelectedMethod] = useState<EXeroPaymentMethod>(
    initMethod || EXeroPaymentMethod.PAY_NOW
  );

  const isSelected = (method: EXeroPaymentMethod): boolean =>
    selectedMethod === method;

  const onSubmit = useCallback(() => {
    if (selectedMethod === EXeroPaymentMethod.PAY_NOW) {
      onPayNowClick();
    } else {
      openModalCreditCard();
      //onCreditCardClick();
    }
  }, [selectedMethod]);

  return (
    <div className="w-full p-6 bg-white shadow-md rounded-lg">
      <div className="w-full flex flex-row justify-between">
        <h3 className="text-xl font-semibold mb-4">Payment Information</h3>
        <ActionIcon size="sm" variant="text" onClick={onClose}>
          <IoMdClose size={24} />
        </ActionIcon>
      </div>

      <div>
        <p className="font-medium mb-2">Payment Methods</p>
        <div className="grid grid-cols-2 gap-4">
          {/* PAY_NOW with image */}
          <div
            className={cn(
              "relative p-4 rounded-lg bg-gray-100 border cursor-pointer transition flex justify-center items-center h-32",
              isSelected(EXeroPaymentMethod.PAY_NOW)
                ? "bg-purple-100 border-purple-600 ring-2 ring-purple-400"
                : "border-gray-300"
            )}
            onClick={() => setSelectedMethod(EXeroPaymentMethod.PAY_NOW)}
          >
            <ImageApp
              inApp
              src={IMAGES.PAY_NOW}
              alt="PAY_NOW"
              className="max-h-16 object-contain"
            />
            <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full flex flex-row items-center">
              <MdPushPin /> Preferred
            </div>
          </div>

          {/* Credit Card (disabled) */}
          <div
            onClick={() => setSelectedMethod(EXeroPaymentMethod.CREDIT_CARD)}
            className={cn(
              "relative p-4 rounded-lg border border-gray-300 bg-gray-100 text-center flex flex-col items-center justify-center cursor-pointer",
              isSelected(EXeroPaymentMethod.CREDIT_CARD)
                ? "bg-purple-100 border-purple-600 ring-2 ring-purple-400"
                : "border-gray-300"
            )}
          >
            <ImCreditCard size={50} />
            <p>Credit Card</p>
          </div>
        </div>
      </div>
      {getContent(selectedMethod)}
      <div className="w-full flex justify-end mt-6">
        <Button onClick={onSubmit}>Submit Order</Button>
      </div>
    </div>
  );
};

export default SelectPaymentMethod;
